import { productSalesMaterialsApi } from '@shared/api/product-sales-materials';
import type {
  ProductSalesMaterial,
  ProductSalesMaterialCreateRequest,
  ProductSalesMaterialUpdateRequest,
} from '@shared/types/product-sales-material';

/// ソリューション詳細「営業支援」タブの資料一覧を管理するストア。
///
/// `ProductSalesMaterialsEditor` (widget) が購読する。load / create / update / delete の
/// mutation は成功後に store state を直接更新する (再 fetch 不要)。
/// `productSalesMaterialsApi` は typed を返すため domain parser は通さない。
interface SalesMaterialsState {
  data: ProductSalesMaterial[];
  loading: boolean;
  error: string | null;
}

const state = $state<SalesMaterialsState>({ data: [], loading: false, error: null });
let loadToken = 0;

export const productSalesMaterialsStore = {
  get data(): ProductSalesMaterial[] {
    return state.data;
  },
  get loading(): boolean {
    return state.loading;
  },
  get error(): string | null {
    return state.error;
  },
  /// 指定商材の資料一覧を取得する。stale は token で破棄。
  async load(productId: string): Promise<void> {
    const token = ++loadToken;
    state.loading = true;
    state.error = null;
    try {
      const res = await productSalesMaterialsApi.list(productId);
      if (token !== loadToken) return;
      state.data = res.items;
    } catch (err) {
      if (token !== loadToken) return;
      state.error = err instanceof Error ? err.message : String(err);
    } finally {
      if (token === loadToken) state.loading = false;
    }
  },
  /// 資料を新規作成する。成功後に store state の末尾へ追加。
  async create(
    productId: string,
    body: ProductSalesMaterialCreateRequest,
  ): Promise<ProductSalesMaterial> {
    const created = await productSalesMaterialsApi.create(productId, body);
    state.data = [...state.data, created];
    return created;
  },
  /// 資料を更新する。成功後に store state の該当行を置換。
  async update(
    productId: string,
    materialId: string,
    body: ProductSalesMaterialUpdateRequest,
  ): Promise<ProductSalesMaterial> {
    const updated = await productSalesMaterialsApi.update(productId, materialId, body);
    state.data = state.data.map((m) => (m.id === updated.id ? updated : m));
    return updated;
  },
  /// 資料を削除する。成功後に store state から除外。
  async delete(productId: string, materialId: string): Promise<void> {
    await productSalesMaterialsApi.delete(productId, materialId);
    state.data = state.data.filter((m) => m.id !== materialId);
  },
  clear(): void {
    loadToken++;
    state.data = [];
    state.error = null;
    state.loading = false;
  },
};
